import { InfiniteData } from '@tanstack/react-query';

import { queryClient } from '@/app/providers/react-query-provider';

import { entityTeamAssociateType, EntityTeamItem, GetEntityTeamRes } from '../entity/types';
import { ENTITY_VIEWS_QUERY_KEYS } from '../entity-views/queries';

interface OptimisticDeleteTeamMemberVariables {
  entityId: string | null;
  memberId?: string | null;
  invitationId?: string | null;
}

export const optimisticDeleteTeamMember = ({
  entityId,
  memberId,
  invitationId,
}: OptimisticDeleteTeamMemberVariables) => {
  const keepItem = (item: EntityTeamItem) =>
    !(memberId && item.entityMemberId === memberId) &&
    !(invitationId && item.entityInvitationId === invitationId);

  queryClient.setQueriesData<GetEntityTeamRes | InfiniteData<GetEntityTeamRes>>(
    { queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId] },
    (old) => {
      if (!old) return old;

      if ('pages' in old) {
        return {
          ...old,
          pages: old.pages.map((page) => {
            const items = page.items.filter(keepItem);
            return { ...page, items, total: items.length };
          }),
        };
      }

      const items = old.items.filter(keepItem);
      return { ...old, items, total: items.length };
    },
  );
};

interface OptimisticCreateFundInvitationVariables {
  entityId: string;
  email: string;
  fundId: string;
  isEntityMember: boolean;
  memberOrInvitationId: string;
}

export const optimisticCreateFundInvitation = ({
  entityId,
  email,
  fundId,
  isEntityMember,
  memberOrInvitationId,
}: OptimisticCreateFundInvitationVariables) => {
  queryClient.setQueriesData<GetEntityTeamRes>(
    { queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId] },
    (old) => {
      if (!old) return old;

      return {
        ...old,
        items: old.items.map((item) =>
          item.email === email
            ? {
                ...item,
                fundAssociations: item.fundAssociations.map((fundAssociation) =>
                  fundAssociation.fundId === fundId
                    ? {
                        ...fundAssociation,
                        title: 'Team Member',
                        type: isEntityMember
                          ? entityTeamAssociateType.Member
                          : entityTeamAssociateType.FundInvitation,
                        memberSince: isEntityMember ? new Date().toISOString() : null,
                        ...(isEntityMember
                          ? { fundMemberId: memberOrInvitationId }
                          : { fundInvitationId: memberOrInvitationId }),
                      }
                    : fundAssociation,
                ),
              }
            : item,
        ),
      };
    },
  );
};

interface OptimisticDeleteFundInvitationVariables {
  entityId: string;
  email: string;
  fundId: string;
}

export const optimisticDeleteFundInvitation = ({
  entityId,
  email,
  fundId,
}: OptimisticDeleteFundInvitationVariables) => {
  queryClient.setQueriesData<GetEntityTeamRes>(
    { queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId] },
    (old) => {
      if (!old) return old;

      return {
        ...old,
        items: old.items.map((item) =>
          item.email === email
            ? {
                ...item,
                fundAssociations: item.fundAssociations.map((fundAssociation) =>
                  fundAssociation.fundId === fundId
                    ? {
                        ...fundAssociation,
                        title: null,
                        type: entityTeamAssociateType.NotAssociate,
                        memberSince: null,
                        fundMemberId: null,
                        fundInvitationId: null,
                      }
                    : fundAssociation,
                ),
              }
            : item,
        ),
      };
    },
  );
};
